(() => {
  const config = window.CEWarrantyPromo;

  if (!config || !Array.isArray(config.pairs)) return;

  const ROOT = window.Shopify?.routes?.root || '/';
  const PROMO_PROPERTY = config.promoTag || '_ce_promo';
  const ASSOCIATION_PROPERTY = config.associationTag || '_ce_abbinato_a';
  const SOURCE = 'ce-warranty-promo';

  let syncing = false;
  let syncTimeout = null;
  let subscribed = false;

  const associations = config.pairs
    .flatMap((pair) =>
      (pair.variants || []).map((variantPair) => {
        const mainVariantId = Number(variantPair.mainVariantId);
        const warrantyVariantId = Number(variantPair.warrantyVariantId);

        return {
          mainProductHandle: pair.mainProductHandle,
          mainVariantId,
          warrantyVariantId,
          associationKey: `${pair.mainProductHandle}:${mainVariantId}`,
        };
      })
    )
    .filter(
      (association) =>
        association.mainProductHandle &&
        association.mainVariantId &&
        association.warrantyVariantId
    );

  const associationByMainVariant = new Map(
    associations.map((association) => [
      association.mainVariantId,
      association,
    ])
  );

  function promoIsActive() {
    return Date.now() < new Date(config.endsAt).getTime();
  }

  async function fetchJSON(url, options = {}) {
    const response = await fetch(url, options);

    let data;

    try {
      data = await response.json();
    } catch (error) {
      throw new Error('Risposta non valida ricevuta da Shopify.');
    }

    if (!response.ok || data?.status >= 400) {
      throw new Error(
        data?.description ||
          data?.message ||
          'Si è verificato un errore durante l’aggiornamento del carrello.'
      );
    }

    return data;
  }

  function getMainProperties(formData) {
    const properties = {};

    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);

      if (match && value !== '') {
        properties[match[1]] = value;
      }
    }

    return properties;
  }

  function getPromoProperties(association) {
    return {
      [PROMO_PROPERTY]: '1',
      [ASSOCIATION_PROPERTY]: association.associationKey,
    };
  }

  function getCartInterface() {
    return (
      document.querySelector('cart-drawer') ||
      document.querySelector('cart-notification')
    );
  }

  function setFormLoading(form, loading) {
    const button = form.querySelector('[type="submit"]');
    const spinner = form.querySelector('.loading__spinner');

    if (button) {
      if (loading) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
      } else {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
      }
    }

    if (spinner) {
      spinner.classList.toggle('hidden', !loading);
    }
  }

  function showFormError(form, message = '') {
    const wrapper = form
      .closest('product-form')
      ?.querySelector('.product-form__error-message-wrapper');

    const messageElement = wrapper?.querySelector(
      '.product-form__error-message'
    );

    if (!wrapper || !messageElement) return;

    wrapper.toggleAttribute('hidden', !message);
    messageElement.textContent = message;
  }

  async function addMainProductWithWarranty(
    form,
    association,
    variantId
  ) {
    const formData = new FormData(form);
    const quantity = parseInt(formData.get('quantity'), 10) || 1;
    const mainProperties = getMainProperties(formData);

    const mainItem = {
      id: variantId,
      quantity,
    };

    if (Object.keys(mainProperties).length) {
      mainItem.properties = mainProperties;
    }

    const sellingPlan = formData.get('selling_plan');

    if (sellingPlan) {
      mainItem.selling_plan = sellingPlan;
    }

    const warrantyItem = {
      id: association.warrantyVariantId,
      quantity,
      properties: getPromoProperties(association),
    };

    const body = {
      items: [mainItem, warrantyItem],
    };

    const cartInterface = getCartInterface();

    if (
      cartInterface &&
      typeof cartInterface.getSectionsToRender === 'function'
    ) {
      body.sections = cartInterface
        .getSectionsToRender()
        .map((section) => section.id);

      body.sections_url = window.location.pathname;
    }

    return fetchJSON(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });
  }

  async function fetchFreshCartSections() {
    const cartInterface = getCartInterface();

    if (
      !cartInterface ||
      typeof cartInterface.getSectionsToRender !== 'function'
    ) {
      return null;
    }

    const sectionIds = cartInterface
      .getSectionsToRender()
      .map((section) => section.id)
      .filter(Boolean);

    if (!sectionIds.length) return null;

    const cartUrl = new URL(`${ROOT}cart`, window.location.origin);
    cartUrl.searchParams.set('sections', sectionIds.join(','));

    const response = await fetch(cartUrl.toString(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(
        'Impossibile aggiornare la visualizzazione del carrello.'
      );
    }

    const sections = await response.json();

    return {
      id: null,
      sections,
    };
  }

  function updateCartDrawerSection(
    cartInterface,
    parsedState,
    openDrawer = false
  ) {
    const sections = parsedState?.sections;

    if (!sections) return false;

    let updatedSomething = false;

    cartInterface
      .getSectionsToRender()
      .forEach((section) => {
        const html = sections[section.id];

        if (typeof html !== 'string' || !html.trim()) {
          return;
        }

        const selector = section.selector || '.shopify-section';
        const parsedHtml = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const sourceElement = parsedHtml.querySelector(selector);
        const targetElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sourceElement || !targetElement) {
          return;
        }

        targetElement.innerHTML = sourceElement.innerHTML;
        updatedSomething = true;
      });

    if (!updatedSomething) return false;

    /*
     * Quando il carrello parte vuoto, Refresh applica la classe
     * "is-empty" al componente <cart-drawer>. Il submit nativo del tema
     * la rimuove dopo il primo inserimento; siccome questo script intercetta
     * il submit, dobbiamo rimuoverla noi.
     */
    cartInterface.classList.remove('is-empty');

    const drawerInner = cartInterface.querySelector('.drawer__inner');
    drawerInner?.classList.remove('is-empty');

    const drawerItems = cartInterface.querySelector('cart-drawer-items');
    drawerItems?.classList.remove('is-empty');

    const overlay = cartInterface.querySelector('#CartDrawer-Overlay');

    if (overlay && typeof cartInterface.close === 'function') {
      overlay.addEventListener(
        'click',
        cartInterface.close.bind(cartInterface)
      );
    }

    if (openDrawer && typeof cartInterface.open === 'function') {
      cartInterface.open();
    }

    return true;
  }

  async function renderCartState(
    parsedState,
    form = null,
    openDrawer = false
  ) {
    const cartInterface = getCartInterface();

    if (!cartInterface) {
      window.location.href = `${ROOT}cart`;
      return;
    }

    const doRender = () => {
      if (cartInterface.matches('cart-drawer')) {
        const rendered = updateCartDrawerSection(
          cartInterface,
          parsedState,
          openDrawer
        );

        if (!rendered) {
          console.warn(
            '[CamperEnergy Garanzia] Sezioni carrello non renderizzabili.',
            parsedState?.sections
          );
        }

        return;
      }

      if (typeof cartInterface.renderContents === 'function') {
        try {
          cartInterface.renderContents(parsedState);
        } catch (error) {
          console.error(
            '[CamperEnergy Garanzia] Errore aggiornamento carrello:',
            error
          );
        }
      }
    };

    const quickAddModal = form?.closest('quick-add-modal');

    if (
      quickAddModal &&
      typeof quickAddModal.hide === 'function'
    ) {
      document.body.addEventListener(
        'modalClosed',
        doRender,
        { once: true }
      );

      quickAddModal.hide(true);
    } else {
      doRender();
    }
  }

  async function getCart() {
    return fetchJSON(`${ROOT}cart.js`);
  }

  async function addPromoWarranty(association, quantity) {
    return fetchJSON(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [
          {
            id: association.warrantyVariantId,
            quantity,
            properties: getPromoProperties(association),
          },
        ],
      }),
    });
  }

  async function changeCartLine(key, quantity) {
    return fetchJSON(`${ROOT}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: key,
        quantity,
      }),
    });
  }

  function getAssociationState(cart, association) {
    const mainQuantity = cart.items
      .filter(
        (item) =>
          Number(item.variant_id) === association.mainVariantId
      )
      .reduce((total, item) => total + item.quantity, 0);

    const promoWarrantyLines = cart.items.filter(
      (item) =>
        Number(item.variant_id) === association.warrantyVariantId &&
        item.properties?.[PROMO_PROPERTY] === '1' &&
        item.properties?.[ASSOCIATION_PROPERTY] ===
          association.associationKey
    );

    const warrantyQuantity = promoWarrantyLines.reduce(
      (total, item) => total + item.quantity,
      0
    );

    return {
      mainQuantity,
      warrantyQuantity,
      promoWarrantyLines,
    };
  }

  async function notifyCartUpdated(cart) {
    if (
      typeof publish === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: SOURCE,
        cartData: cart,
      });
    }
  }

  async function syncAssociation(association, cart) {
    let currentCart = cart;
    let changed = false;
    let safetyCounter = 0;

    while (safetyCounter < 10) {
      safetyCounter += 1;

      const state = getAssociationState(
        currentCart,
        association
      );

      const desiredQuantity = promoIsActive()
        ? state.mainQuantity
        : 0;

      if (state.warrantyQuantity === desiredQuantity) {
        return {
          cart: currentCart,
          changed,
        };
      }

      if (state.warrantyQuantity < desiredQuantity) {
        const quantityToAdd =
          desiredQuantity - state.warrantyQuantity;

        await addPromoWarranty(
          association,
          quantityToAdd
        );

        changed = true;
        currentCart = await getCart();
        continue;
      }

      const excess =
        state.warrantyQuantity - desiredQuantity;

      const line =
        state.promoWarrantyLines[
          state.promoWarrantyLines.length - 1
        ];

      if (!line) {
        return {
          cart: currentCart,
          changed,
        };
      }

      const newQuantity = Math.max(
        0,
        line.quantity - excess
      );

      await changeCartLine(
        line.key,
        newQuantity
      );

      changed = true;
      currentCart = await getCart();
    }

    return {
      cart: currentCart,
      changed,
    };
  }

  async function syncWarrantyCart(refreshInterface = false) {
    if (syncing) return;

    syncing = true;

    try {
      let cart = await getCart();
      let changed = false;

      for (const association of associations) {
        const result = await syncAssociation(
          association,
          cart
        );

        cart = result.cart;

        if (result.changed) {
          changed = true;
        }
      }

      if (changed) {
        cart = await getCart();

        if (refreshInterface) {
          const freshState = await fetchFreshCartSections();

          if (freshState) {
            await renderCartState(
              freshState,
              null,
              false
            );
          }
        }

        notifyCartUpdated(cart);
      }
    } catch (error) {
      console.error(
        '[CamperEnergy Garanzia]',
        error
      );
    } finally {
      syncing = false;
    }
  }

  function scheduleCartSync() {
    clearTimeout(syncTimeout);

    syncTimeout = setTimeout(() => {
      syncWarrantyCart(true);
    }, 150);
  }

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;

      if (
        !(form instanceof HTMLFormElement) ||
        !form.matches('form[action*="/cart/add"]')
      ) {
        return;
      }

      if (!promoIsActive()) return;

      const formData = new FormData(form);
      const variantId = Number(formData.get('id'));

      const association =
        associationByMainVariant.get(variantId);

      if (!association) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      showFormError(form);
      setFormLoading(form, true);

      try {
        const response =
          await addMainProductWithWarranty(
            form,
            association,
            variantId
          );

        await renderCartState(
          response,
          form,
          true
        );

        const cart = await getCart();
        notifyCartUpdated(cart);
      } catch (error) {
        console.error(
          '[CamperEnergy Garanzia]',
          error
        );

        showFormError(
          form,
          error.message
        );
      } finally {
        setFormLoading(form, false);
      }
    },
    true
  );

  function initializeCartSubscription(attempt = 0) {
    if (subscribed) return;

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      subscribed = true;

      subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          if (event?.source === SOURCE) {
            return;
          }

          scheduleCartSync();
        }
      );

      syncWarrantyCart(false);
      return;
    }

    if (attempt < 50) {
      setTimeout(() => {
        initializeCartSubscription(
          attempt + 1
        );
      }, 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeCartSubscription();
      },
      { once: true }
    );
  } else {
    initializeCartSubscription();
  }
})();
